import { Button, Tooltip } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import AddShoppingCartIcon from '@material-ui/icons/AddShoppingCartTwoTone';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { ProductQuantityLimits } from '../../@sprinx/knihovka-types/products';
import QuantityField, { QuantityFieldProps } from '../QuantityField';
import { ProductStockQuantityProduct, useProductStockQuantity } from '../../api/products/productStockInfo';
import { monitorStockAvailability } from '../../config';
import { catalogueViewModeState, hasPrice } from '../../api/products/catalogue';
import useTranslateWithFallback from '../../hooks/useTranslateWithFallback';

export interface AddToCartButtonToQtyProps extends ProductQuantityLimits {
  classess?: any;
  onAddToCart: (nextQuantity: number) => Promise<any>;
  product: ProductStockQuantityProduct;
  quantity?: number;
  QuantityFieldProps?: Partial<QuantityFieldProps>;
  refButton?: React.Ref<any>;
  refInput?: React.Ref<any>;
}

export type AddToCartButtonToQtyClassKey = 'root' | 'buttonPadding' | 'addToCart';

const themeSettings = { name: 'AddToCartButtonToQty' };
const useStyles = makeStyles(
  createStyles<AddToCartButtonToQtyClassKey, {}>({
    root: {},
    buttonPadding: {
      paddingRight: 0,
    },
    addToCart: {
      height: '32px',
      backgroundColor: '#1D1D1B',
      color: 'white',
      '&:hover': {
        backgroundColor: '#786C3A',
      },
      '& span': {
        margin: 0,
      },
    },
  }),
  themeSettings,
);

const AddToCartButtonToQty: React.FC<AddToCartButtonToQtyProps> = ({
  onAddToCart,
  product,
  quantity,
  quantityDefault,
  quantityMax,
  quantityMin,
  quantityStep,
  quantityStepRounding,
  QuantityFieldProps: pQuantityFieldProps,
  refButton,
  refInput,
  classess,
}) => {
  const classes = useStyles({ classess });
  const [isDisabled, setDisabled] = React.useState(false);
  const [addQuantity, setAddQuantity] = React.useState(quantityDefault);
  const [onStockQty] = useProductStockQuantity(product);
  const catalogueView = useRecoilValue(catalogueViewModeState);

  const unAvailableDueToMinQuantity = React.useMemo(() => {
    return monitorStockAvailability ? (product.quantityStep || 1) > onStockQty : false;
  }, [onStockQty, product.quantityStep]);

  const handleButtonClick = React.useCallback(async () => {
    setDisabled(true);
    await onAddToCart(quantityStep);
    setDisabled(false);
    setAddQuantity(quantityMin);
  }, [onAddToCart, quantityMin, quantityStep]);

  const handleQuantityChange = (current: number) => {
    setAddQuantity(current);
  };

  const handleAddToCart = (current: number) => {
    onAddToCart(current);
  };

  const handleKeyDown = (event: any) => {
    if (event && event.key === 'Enter') {
      // target.value == onchage, addQuantity == +-
      const current = event.target && event.target.value ? event.target.value : addQuantity;

      if (current >= quantityMin) {
        event.preventDefault();
        onAddToCart(current);
      }
    }
  };

  // additional check for product price to future purposes
  if (!hasPrice(product) || !product.taxonomies?.includes('/catalogue')) {
    return null;
  }

  if (monitorStockAvailability && onStockQty <= 0) {
    return <ButtonAddToCart className={classes} ref={refButton} disabled={true} />;
  }

  return (quantity || 0) > 0 ? (
    <QuantityField
      ref={refInput}
      disabled={unAvailableDueToMinQuantity}
      quantity={quantity}
      onChange={handleAddToCart}
      quantityMax={quantityMax}
      quantityMin={quantityMin}
      quantityStep={quantityStep}
      quantityStepRounding={quantityStepRounding}
      fullWidth
      {...pQuantityFieldProps}
    />
  ) : (
    <>
      {catalogueView === 'row' && (
        <div>
          <QuantityField
            className={classes.buttonPadding}
            disabled={isDisabled || unAvailableDueToMinQuantity}
            ref={refInput}
            quantity={addQuantity}
            onChange={handleQuantityChange}
            quantityMax={quantityMax}
            quantityMin={quantityMin}
            quantityStep={quantityStep}
            quantityStepRounding={quantityStepRounding}
            onKeyDown={handleKeyDown}
            fullWidth
            {...pQuantityFieldProps}
          />
        </div>
      )}
      <ButtonAddToCart
        className={classes}
        ref={refButton}
        disabled={isDisabled || unAvailableDueToMinQuantity}
        onClick={handleButtonClick}
      />
    </>
  );
};

AddToCartButtonToQty.displayName = 'AddToCartButtonToQty';

export default AddToCartButtonToQty;

interface ButtonAddToCart {
  className: any;
  disabled?: boolean;
  onClick?: React.EventHandler<React.MouseEvent>;
}

const ButtonAddToCart = React.forwardRef<HTMLButtonElement, ButtonAddToCart>(
  ({ className, disabled, onClick }, ref) => {
    const classes = useStyles({ className });
    const t = useTranslateWithFallback();

    return disabled ? (
      <Button
        ref={ref}
        className={classes.addToCart}
        variant='contained'
        disabled={disabled}
        onClick={onClick}
        startIcon={<AddShoppingCartIcon />}
      />
    ) : (
      <Tooltip title={t('app/addToCart')}>
        <Button
          ref={ref}
          className={classes.addToCart}
          variant='contained'
          disabled={disabled}
          onClick={onClick}
          startIcon={<AddShoppingCartIcon />}
        />
      </Tooltip>
    );
  },
);

ButtonAddToCart.displayName = 'ButtonAddToCart';
